



class MongoQueryBuilder {
    constructor({
                    conditions = undefined,

    } = {}) {
        console.log("Creating new MongoQueryBuilder", conditions)
        this.conditions = [];
        this.customConditions = [];
        this.projectionFields = {};
        this.limit = null; // Initialize limit
        this.sortConditions = {}; // Initialize sort conditions
        if (conditions) {
            conditions.forEach(condition => {
                this.addCondition(condition);
            });
        }
    }

    /**
     * Adds a condition to the query builder. Each condition is used to build the MongoDB query.
     * Optionally, adds the field to the projection fields object if `project` is true.
     *
     * @param {Object} condition - The condition to add.
     * @param {string} condition.fullPath - The document field path.
     * @param {string} condition.operator - The MongoDB operator (e.g., '$in', '$gt').
     * @param {any} condition.value - The value for the condition.
     * @param {Object} [options] - Additional options.
     * @param {boolean} [options.project=true] - Whether to include the field in the result projection.
     */
    addCondition(condition, {project=true}={}) {
        // Check if the operator is an array operator ($in or $nin)
        if (['$in', '$nin'].includes(condition.operator)) {
            this.conditions.push(condition);
        } else {
            // For non-array operators, keep the existing logic
            this.conditions.push(condition);
        }

        // If project is true, add the field to the projectionFields object
        if (project) {
            this.projectionFields[condition.fullPath] = 1;
        }
    }

    /**
     * Adds an $in query condition for the specified key and list of values.
     *
     * @param {string} key - The document field path to query.
     * @param {Array} list - The array of values for the $in condition.
     */
    inList(key, list) {
        // Create an $in query and add it to conditions
        this.addCondition({
            fullPath: key,
            operator: '$in',
            value: list,
        });
    }

    /**
     * Sets a limit for the number of documents to return in the query result.
     *
     * @param {number} limit - The maximum number of documents to return.
     */
    setLimit(limit) {
        this.limit = Number(limit); // Ensure limit is stored as a number
    }

    /**
     * Sets the sort conditions for the query result.
     *
     * @param {Object} sortFields - The fields to sort by and their sort directions (1 for ascending, -1 for descending).
     */
    setSort(sortFields) {
        this.sortConditions = sortFields; // Expect sortFields to be an object like { field: 1 } for ascending or { field: -1 } for descending
    }

    updateProjection(projectionFields) {
        projectionFields.forEach(field => {
            this.projectionFields[field] = 1;
        })
    }


    toMongoQuery() {
        // Initialize an empty match object
        const matchConditions = {};

        // Iterate over conditions to populate the match object
        this.conditions.forEach(({ fullPath, operator, value }) => {
            const mongoOperator = this.mapOperatorToMongo(operator);
            // Check if the fullPath already has a condition, merge if it does
            if (matchConditions[fullPath]) {
                Object.assign(matchConditions[fullPath], { [mongoOperator]: this.parseValue(value) });
            } else {
                matchConditions[fullPath] = { [mongoOperator]: this.parseValue(value) };
            }
        });
        if (this.customConditions.length > 0) {
            this.customConditions.forEach(condition => {
                Object.assign(matchConditions, condition)
            })
        }
        // Construct the query array with a $match stage and optionally a $project stage
        const query = [{ $match: matchConditions }];
        // Check if there are any fields to project
        if (Object.keys(this.projectionFields).length > 0) {
            query.push({ $project: this.projectionFields });
        }
        if (Object.keys(this.sortConditions).length > 0) {
            query.push({ $sort: this.sortConditions });
        }
        if (this.limit !== null) {
            query.push({ $limit: this.limit });
        }

        return query;
    }

    mapOperatorToMongo(operator) {
        // Map the UI operator to MongoDB's query operators, including the $in operator
        const operatorMap = {
            '>': '$gt',
            '<': '$lt',
            '=': '$eq',
            '$in': '$in', // Include $in in the operator map
            '$nin': '$nin'

        };
        return operatorMap[operator] || '$eq';
    }

    parseValue(value) {
        console.log("parseValue", value)

        // Parse value to appropriate type (number, string, etc.)
        if (!isNaN(value) && value?.trim?.() !== '') return Number(value);
        return value;
    }

    static fromMongoQuery(jsonQuery) {
        const query = JSON.parse(jsonQuery);
        const builder = new MongoQueryBuilder();

        query.forEach(stage => {
            if (stage.$match) {
                Object.entries(stage.$match).forEach(([fullPath, condition]) => {
                    const [operator, value] = Object.entries(condition)[0];
                    builder.addCondition({
                        fullPath,
                        operator: builder.reverseMapOperator(operator),
                        value
                    });
                });
            }

            if (stage.$project) {
                builder.projectionFields = stage.$project;
            }

            if (stage.$sort) {
                builder.setSort(stage.$sort);
            }

            if (stage.$limit) {
                builder.setLimit(stage.$limit);
            }
        });

        return builder;
    }

    reverseMapOperator(mongoOperator) {
        const reverseOperatorMap = {
            '$gt': '>',
            '$lt': '<',
            '$eq': '=',
            '$in': '$in',
            '$nin': '$nin'  // Added $nin operator
        };
        return reverseOperatorMap[mongoOperator] || '=';
    }
}

export { MongoQueryBuilder };
